import { Box, Button, Heading, Table } from "@chakra-ui/react-v3";
import { DownloadIcon } from "@chakra-ui/icons";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { useState, useEffect, useRef } from "react";
import { useDisclosure } from "@chakra-ui/react-v3";
import { TableRow } from "../../utils/tables-metadata";
import { DocumentReferenceRowData } from "./raw-documents-rows";
import { HighlightedText } from "../../components/highlighted-text";
import { useDownloadFile } from "../../../shared-logic/useDownloadFile";

export type RawDocumentsTableProps = {
  rows: TableRow[];
  onRowClick: (content: React.ReactNode) => void;
  fhirSdk: FhirBundleSdk;
  filterQuery: string;
};

function isXml(mimeType: string | undefined): boolean {
  return mimeType ? mimeType.includes("xml") : false;
}

export function RawDocumentsTable({ rows, filterQuery }: RawDocumentsTableProps) {
  const { onDownloadFile, isDownloading } = useDownloadFile();
  const { onOpen: onMenuOpen, onClose: onMenuClose } = useDisclosure();
  const [currentDocument, setCurrentDocument] = useState<{
    id: string;
    fileName: string;
    fileType: string;
    positionX: number;
    positionY: number;
  } | null>(null);

  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (currentDocument) {
      const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
          onMenuClose();
          setCurrentDocument(null);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }

    return undefined;
  }, [currentDocument, onMenuClose]);

  function handleRowClick(row: TableRow, event?: React.MouseEvent<HTMLElement>) {
    const docRow = row as unknown as DocumentReferenceRowData;

    const clientX = event?.clientX ?? window.innerWidth / 2;
    const clientY = event?.clientY ?? window.innerHeight / 2;

    setCurrentDocument({
      id: docRow.id,
      fileName: docRow.fileName,
      fileType: docRow.fileType,
      positionX: clientX,
      positionY: clientY,
    });
    onMenuOpen();
  }

  if (rows.length === 0) return null;

  return (
    <>
      <Box
        w="100%"
        mb={2}
        borderRadius="lg"
        bg="bg.surface"
        px={6}
        py={6}
        border="1px"
        borderColor="border"
        boxShadow="sm"
      >
        <Heading size="md" fontWeight="semibold" color="fg" mb={6}>
          Raw Documents ({rows.length})
        </Heading>

        <Table.Root size="sm" variant="line" width="100%">
          <Table.Header bg="bg.panel">
            <Table.Row>
              <Table.ColumnHeader fontWeight="bold" fontSize="sm" color="fg" py={3} px={4}>
                Description
              </Table.ColumnHeader>
              <Table.ColumnHeader fontWeight="bold" fontSize="sm" color="fg" py={3} px={4}>
                Size
              </Table.ColumnHeader>
              <Table.ColumnHeader fontWeight="bold" fontSize="sm" color="fg" py={3} px={4}>
                File Type
              </Table.ColumnHeader>
              <Table.ColumnHeader fontWeight="bold" fontSize="sm" color="fg" py={3} px={4}>
                Organization
              </Table.ColumnHeader>
              <Table.ColumnHeader fontWeight="bold" fontSize="sm" color="fg" py={3} px={4}>
                Date
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map(row => {
              const docRow = row as unknown as DocumentReferenceRowData;
              return (
                <Table.Row
                  key={row.id}
                  cursor="pointer"
                  onClick={event => handleRowClick(row, event)}
                  _hover={{ bg: "interactive.hover" }}
                  borderBottom="1px"
                  borderColor="border.subtle"
                >
                  <Table.Cell fontSize="sm" py={3} px={4} color="fg.muted">
                    <HighlightedText text={docRow.description} searchQuery={filterQuery} />
                  </Table.Cell>
                  <Table.Cell fontSize="sm" py={3} px={4} color="fg.muted">
                    <HighlightedText text={docRow.size} searchQuery={filterQuery} />
                  </Table.Cell>
                  <Table.Cell fontSize="sm" py={3} px={4} color="fg.muted">
                    <HighlightedText text={docRow.fileType} searchQuery={filterQuery} />
                  </Table.Cell>
                  <Table.Cell fontSize="sm" py={3} px={4} color="fg.muted">
                    <HighlightedText text={docRow.organization} searchQuery={filterQuery} />
                  </Table.Cell>
                  <Table.Cell fontSize="sm" py={3} px={4} color="fg.muted">
                    <HighlightedText text={docRow.date} searchQuery={filterQuery} />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      </Box>

      {/* Document menu popup */}
      {currentDocument && (
        <Box
          ref={menuRef}
          position="fixed"
          top={currentDocument.positionY + 10}
          left={currentDocument.positionX - 120}
          zIndex={1001}
          bg="bg.surface"
          border="1px solid"
          borderColor="border.emphasized"
          borderRadius="md"
          p={2}
          boxShadow="lg"
          minW="120px"
          data-menu="document-menu"
        >
          {isXml(currentDocument.fileType) ? (
            <>
              <Button
                onClick={() => {
                  onDownloadFile({
                    docId: currentDocument.id,
                    docFilename: currentDocument.fileName,
                    docExtension: currentDocument.fileType,
                    conversionType: "pdf",
                  });
                  onMenuClose();
                  setCurrentDocument(null);
                }}
                loading={
                  isDownloading[currentDocument.id]?.downloading &&
                  isDownloading[currentDocument.id]?.type === "pdf"
                }
                size="sm"
                variant="ghost"
                w="full"
                justifyContent="flex-start"
              >
                <DownloadIcon mr={2} />
                PDF
              </Button>
              <Button
                onClick={() => {
                  onDownloadFile({
                    docId: currentDocument.id,
                    docFilename: currentDocument.fileName,
                    docExtension: currentDocument.fileType,
                    conversionType: "html",
                  });
                  onMenuClose();
                  setCurrentDocument(null);
                }}
                loading={
                  isDownloading[currentDocument.id]?.downloading &&
                  isDownloading[currentDocument.id]?.type === "html"
                }
                size="sm"
                variant="ghost"
                w="full"
                justifyContent="flex-start"
              >
                <DownloadIcon mr={2} />
                HTML
              </Button>
              <Button
                onClick={() => {
                  onDownloadFile({
                    docId: currentDocument.id,
                    docFilename: currentDocument.fileName,
                    docExtension: currentDocument.fileType,
                  });
                  onMenuClose();
                  setCurrentDocument(null);
                }}
                loading={
                  isDownloading[currentDocument.id]?.downloading &&
                  isDownloading[currentDocument.id]?.type === "xml"
                }
                size="sm"
                variant="ghost"
                w="full"
                justifyContent="flex-start"
              >
                <DownloadIcon mr={2} />
                XML
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                onDownloadFile({
                  docId: currentDocument.id,
                  docFilename: currentDocument.fileName,
                  docExtension: currentDocument.fileType,
                });
                onMenuClose();
                setCurrentDocument(null);
              }}
              loading={isDownloading[currentDocument.id]?.downloading}
              size="sm"
              variant="ghost"
              w="full"
              justifyContent="flex-start"
            >
              <DownloadIcon mr={2} />
              Download
            </Button>
          )}
        </Box>
      )}
    </>
  );
}
